import React, { useEffect, useState, useCallback } from "react";
import { siteData } from "../mock";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

export default function Testimonials() {
  const items = siteData.testimonials;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((i) => {
    setIndex(((i % items.length) + items.length) % items.length);
  }, [items.length]);

  const next = useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1), [index, goTo]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [paused, items.length]);

  // Show 3 visible cards on desktop, 1 on mobile
  const visible = [
    items[index % items.length],
    items[(index + 1) % items.length],
    items[(index + 2) % items.length],
  ];

  return (
    <section
      id="testimonials"
      className="relative bg-[#0a0a0a] py-24"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute inset-0 bg-grid-lines opacity-30 pointer-events-none" />
      <div className="absolute top-1/2 -translate-y-1/2 -right-40 w-[500px] h-[500px] bg-red-700/15 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="deal-yellow font-heading font-semibold tracking-[0.3em] text-sm md:text-base mb-3">
              REAL RESULTS
            </p>
            <h2 className="font-display text-white font-bold text-5xl md:text-7xl tracking-wide">
              CLIENT STORIES
            </h2>
            <p className="text-white/70 text-lg mt-4 max-w-xl">
              Everyday people. Extraordinary transformations. This is what happens when you show up.
            </p>
          </div>

          {/* Nav buttons */}
          <div className="flex gap-3">
            <button
              onClick={prev}
              aria-label="Previous testimonial"
              className="w-12 h-12 rounded-md border border-white/15 flex items-center justify-center text-white hover:bg-[#e10600] hover:border-[#e10600] transition-colors"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={next}
              aria-label="Next testimonial"
              className="w-12 h-12 rounded-md border border-white/15 flex items-center justify-center text-white hover:bg-[#e10600] hover:border-[#e10600] transition-colors"
            >
              <ChevronRight size={22} />
            </button>
          </div>
        </div>

        {/* Desktop: 3 cards */}
        <div className="hidden md:grid grid-cols-3 gap-6">
          {visible.map((t, i) => (
            <TestimonialCard key={`${t.id}-${index}-${i}`} t={t} highlighted={i === 0} />
          ))}
        </div>

        {/* Mobile: 1 card */}
        <div className="md:hidden">
          <TestimonialCard t={items[index]} highlighted={true} />
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-10">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to testimonial ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === index ? "w-10 bg-[#e10600]" : "w-2 bg-white/25 hover:bg-white/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ t, highlighted }) {
  return (
    <div
      className={`relative card-surface rounded-2xl p-8 h-full flex flex-col animate-fade-up ${
        highlighted ? "border-[#e10600]/60 shadow-[0_20px_60px_-20px_rgba(225,6,0,0.35)]" : ""
      }`}
    >
      <Quote className="absolute top-6 right-6 text-white/10" size={44} />

      {/* Stars */}
      <div className="flex gap-1 mb-4">
        {Array.from({ length: t.rating }).map((_, i) => (
          <Star key={i} size={16} className="deal-yellow fill-current" />
        ))}
      </div>

      {/* Result badge */}
      <div className="inline-flex self-start px-3 py-1 rounded-md bg-[#2a0000] border border-[#e10600]/40 mb-5">
        <span className="deal-yellow font-heading tracking-[0.15em] text-[11px] font-semibold uppercase">
          {t.result}
        </span>
      </div>

      {/* Quote text */}
      <p className="text-white/85 leading-relaxed text-[15px] flex-1 mb-6">
        &ldquo;{t.quote}&rdquo;
      </p>

      {/* Author */}
      <div className="flex items-center gap-4 pt-5 border-t border-white/10">
        <div className="w-12 h-12 rounded-full bg-[#e10600] flex items-center justify-center font-display font-bold text-white text-lg">
          {t.initials}
        </div>
        <div>
          <p className="text-white font-heading font-semibold tracking-wide text-base">{t.name}</p>
          <p className="text-white/55 text-xs tracking-[0.15em] uppercase">{t.role}</p>
        </div>
      </div>
    </div>
  );
}
